import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useStores } from "@/lib/stores";
import { logError } from "@/lib/logger";
import OSEditModal from "./OSEditModal";
import KanbanColumn from "./kanban/KanbanColumn";
import { whatsAppLink } from "@/components/WhatsAppButton";
import { RefreshCw, Bell, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { playChime } from "@/lib/chime";

const COLUMNS = [
  { key: "Solicitação de Reparo", tone: "solicitacao", hint: "Aguardando técnico aceitar" },
  { key: "Orçamento", tone: "orcamento", hint: "Recebido, aguardando diagnóstico" },
  { key: "Aguardando Autorização", tone: "aguardando", hint: "Cliente precisa aprovar orçamento" },
  { key: "Serviço Concluído", tone: "concluido", hint: "Pronto para retirada / pagamento" },
];

const POLL_MS = 20000; // 20s

export default function AdminBoard() {
  const { activeStore, activeStoreObj } = useStores();
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragOs, setDragOs] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("gtec_kanban_sound") !== "0");
  const seenRef = useRef(null); // Set of OS codes already seen; null = not initialized yet

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (activeStore) qs.append("store_id", activeStore);
      const { data } = await api.get(`/admin/service-orders${qs.toString() ? "?" + qs.toString() : ""}`);
      setRows(data);

      // Detect brand-new OS in the "Orçamento" column since last poll
      const currentCodes = new Set(data.map((r) => r.os_code));
      if (seenRef.current === null) {
        seenRef.current = currentCodes; // first load — no chime
      } else {
        const newIncoming = data.filter(
          (r) => !seenRef.current.has(r.os_code) && (r.status === "Orçamento" || r.status === "Solicitação de Reparo")
        );
        if (newIncoming.length > 0 && soundOn) {
          playChime();
          const first = newIncoming[0];
          const name = (first.customer_name || "Novo cliente").trim();
          toast.success(`Nova OS ${first.os_code} · ${name}`, {
            description: `${first.device_brand || ""} ${first.device_model || ""}`.trim() + (newIncoming.length > 1 ? ` (+${newIncoming.length - 1} outra${newIncoming.length > 2 ? "s" : ""})` : ""),
          });
        }
        seenRef.current = currentCodes;
      }
    } catch (err) {
      if (!silent) logError("[board] load failed:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    seenRef.current = null; // reset when store filter changes so we don't chime old OS
    load();
    // Pause polling when the tab is hidden — saves requests when many admins keep tab open.
    const tick = () => { if (!document.hidden) load({ silent: true }); };
    const t = setInterval(tick, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStore]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("gtec_kanban_sound", next ? "1" : "0");
    if (next) {
      // Play a test chime so the user knows it's on and the browser unlocks audio for future auto-plays
      playChime();
      toast.success("Som ligado — vamos avisar quando chegar nova OS");
    } else {
      toast.info("Som desligado");
    }
  };

  const grouped = useMemo(() => {
    const g = { "Solicitação de Reparo": [], "Orçamento": [], "Aguardando Autorização": [], "Serviço Concluído": [] };
    for (const r of rows) if (g[r.status]) g[r.status].push(r);
    return g;
  }, [rows]);

  const moveOs = async (osCode, newStatus) => {
    const os = rows.find((r) => r.os_code === osCode);
    if (!os || os.status === newStatus) return;
    // Solicitação de Reparo → Orçamento must go through the "Aceitar" endpoint
    // (sends confirmation email); block drag-and-drop directly to Orçamento.
    if (os.status === "Solicitação de Reparo") {
      toast.error("Clique em ‘Aceitar solicitação’ no card para movê-la para Orçamento.");
      return;
    }
    const prevStatus = os.status;
    // Optimistic update
    setRows((prev) => prev.map((r) => (r.os_code === osCode ? { ...r, status: newStatus } : r)));
    try {
      await api.patch(`/admin/service-orders/${osCode}`, { status: newStatus });
      toast.success(`${osCode} → ${newStatus}`);
    } catch (e) {
      // Rollback
      setRows((prev) => prev.map((r) => (r.os_code === osCode ? { ...r, status: prevStatus } : r)));
      toast.error(e.response?.data?.detail || "Não foi possível mover");
    }
  };

  const acceptRequest = async (osCode, evt) => {
    evt.stopPropagation();
    const os = rows.find((r) => r.os_code === osCode);
    try {
      const { data } = await api.post(`/admin/service-orders/${osCode}/accept-request`);
      toast.success(`${osCode} aceita — cliente foi notificado por e-mail`);
      // Server renders the WhatsApp message from the editable template
      // (/admin/emails → "WhatsApp: Solicitação aceita"). Fallback keeps behaviour
      // working even if the field is missing (older API version, empty template).
      if (os?.customer_phone) {
        const fallback = `Olá ${(os.customer_name || "").trim()}, tudo bem? Aqui é da GTec — aceitamos sua solicitação de reparo (${osCode}).`;
        const message = (data && data.whatsapp_message) || fallback;
        const url = whatsAppLink(os.customer_phone, message);
        if (url) window.open(url, "_blank", "noopener,noreferrer");
      }
      load({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao aceitar solicitação");
    }
  };

  const onDragStart = (e, os) => {
    setDragOs(os);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", os.os_code); }
    catch (err) { logError("[board] setData failed:", err); }
  };
  const onDragEnd = () => { setDragOs(null); setHoverCol(null); };
  const onColDragOver = (e, colKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (hoverCol !== colKey) setHoverCol(colKey);
  };
  const onColDragLeave = (e, colKey) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (hoverCol === colKey) setHoverCol(null);
  };
  const onColDrop = (e, colKey) => {
    e.preventDefault();
    const code = e.dataTransfer.getData("text/plain") || dragOs?.os_code;
    setHoverCol(null); setDragOs(null);
    if (code) moveOs(code, colKey);
  };

  return (
    <div className="p-8 max-w-[1600px]">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Fila do técnico{activeStoreObj ? ` · ${activeStoreObj.name}` : " · Todas as lojas"}</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Kanban</h1>
          <p className="mt-2 text-sm text-neutral-600">Arraste um card entre colunas para mudar o status. Clique para abrir a edição completa.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={toggleSound} title={soundOn ? "Som ligado" : "Som desligado"} data-testid="board-sound-toggle" className={soundOn ? "" : "text-neutral-400"}>
            {soundOn ? <Bell size={14} className="mr-1.5"/> : <BellOff size={14} className="mr-1.5"/>}
            <span className="hidden sm:inline">{soundOn ? "Som ligado" : "Som mudo"}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => load()} data-testid="board-refresh"><RefreshCw size={14} className="mr-1.5"/> Atualizar</Button>
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {COLUMNS.map((col) => (
          <KanbanColumn
            key={col.key}
            column={col}
            orders={grouped[col.key]}
            loading={loading}
            isHover={hoverCol === col.key && dragOs && dragOs.status !== col.key}
            dragOs={dragOs}
            onDragOver={onColDragOver}
            onDragLeave={onColDragLeave}
            onDrop={onColDrop}
            onCardDragStart={onDragStart}
            onCardDragEnd={onDragEnd}
            onCardClick={setSelected}
            onCardAccept={acceptRequest}
          />
        ))}
      </div>

      {selected && <OSEditModal osCode={selected} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}
